import { appName } from "@/lib/constants";

type LogLevel = "debug" | "error" | "info" | "silent" | "warn";

type LogContext = Record<string, unknown>;

type LogEntry = {
  context?: LogContext;
  level: Exclude<LogLevel, "silent">;
  message: string;
  timestamp: string;
};

const LOG_LIMIT = 200;

const levelSeverity: Record<LogLevel, number> = {
  debug: 10,
  error: 40,
  info: 20,
  silent: 50,
  warn: 30,
};

declare global {
  // biome-ignore lint/style/useConsistentTypeDefinitions: Global augmentation requires interface.
  interface Window {
    __UMMAH_WAHIDAH_LOGS__?: LogEntry[];
  }
}

const isBrowser = typeof window !== "undefined";

const normalizeLogLevel = (value: string | undefined): LogLevel => {
  if (!value) {
    return "info";
  }

  const normalized = value.toLowerCase();

  if (normalized === "debug" || normalized === "info" || normalized === "warn" || normalized === "error") {
    return normalized;
  }

  return "info";
};

const resolveLogLevel = (): LogLevel => {
  const configuredLevel = normalizeLogLevel(import.meta.env.VITE_LOG_LEVEL);

  if (configuredLevel !== "info") {
    return configuredLevel;
  }

  if (import.meta.env.MODE === "test") {
    return "warn";
  }

  return import.meta.env.DEV ? "debug" : "info";
};

const currentLogLevel = resolveLogLevel();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const serializeValue = (value: unknown, depth = 0): unknown => {
  if (depth > 2) {
    return "[max-depth]";
  }

  if (value instanceof Error) {
    return {
      message: value.message,
      name: value.name,
      stack: value.stack,
    };
  }

  if (Array.isArray(value)) {
    return value.slice(0, 10).map((entry) => serializeValue(entry, depth + 1));
  }

  if (isRecord(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, serializeValue(entry, depth + 1)]));
  }

  if (
    value === null ||
    typeof value === "boolean" ||
    typeof value === "number" ||
    typeof value === "string" ||
    typeof value === "undefined"
  ) {
    return value;
  }

  return String(value);
};

const normalizeContext = (context?: LogContext): LogContext | undefined => {
  if (!context) {
    return undefined;
  }

  return Object.fromEntries(Object.entries(context).map(([key, value]) => [key, serializeValue(value)]));
};

const shouldLog = (level: Exclude<LogLevel, "silent">) => levelSeverity[level] >= levelSeverity[currentLogLevel];

const storeLogEntry = (entry: LogEntry) => {
  if (!isBrowser) {
    return;
  }

  const currentEntries = window.__UMMAH_WAHIDAH_LOGS__ ?? [];
  window.__UMMAH_WAHIDAH_LOGS__ = [...currentEntries, entry].slice(-LOG_LIMIT);
};

const writeToConsole = (entry: LogEntry) => {
  const prefix = `[${appName}] ${entry.message}`;
  const consoleMethod =
    entry.level === "debug"
      ? console.debug
      : entry.level === "info"
        ? console.info
        : entry.level === "warn"
          ? console.warn
          : console.error;

  if (entry.context) {
    consoleMethod(prefix, entry.context);
    return;
  }

  consoleMethod(prefix);
};

const emit = (level: Exclude<LogLevel, "silent">, message: string, context?: LogContext) => {
  if (!shouldLog(level)) {
    return;
  }

  const entry: LogEntry = {
    context: normalizeContext(context),
    level,
    message,
    timestamp: new Date().toISOString(),
  };

  writeToConsole(entry);
  storeLogEntry(entry);
};

let observabilityInitialized = false;

export const logger = {
  captureError: (message: string, error: unknown, context?: LogContext) => {
    emit("error", message, {
      ...context,
      error,
    });
  },
  debug: (message: string, context?: LogContext) => emit("debug", message, context),
  error: (message: string, context?: LogContext) => emit("error", message, context),
  info: (message: string, context?: LogContext) => emit("info", message, context),
  warn: (message: string, context?: LogContext) => emit("warn", message, context),
};

export const setupGlobalObservability = () => {
  if (!isBrowser || observabilityInitialized) {
    return;
  }

  observabilityInitialized = true;

  window.addEventListener("error", (event) => {
    logger.captureError("Unhandled window error", event.error ?? event.message, {
      colno: event.colno,
      filename: event.filename,
      lineno: event.lineno,
      message: event.message,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    logger.captureError("Unhandled promise rejection", event.reason);
  });

  logger.info("Observability initialized", {
    logLevel: currentLogLevel,
    mode: import.meta.env.MODE,
  });
};
